'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, Check, Loader2, FileText } from 'lucide-react';
import { type ClaimSettings } from '@/lib/claimStore';
import { updateClaim } from '@/lib/claimService';
import { useClaimContext } from '@/contexts/ClaimContext';

type SaveState = 'idle' | 'saving' | 'saved';

const EMPTY_SETTINGS: ClaimSettings = {
  policyNumber: '',
  insuranceCompany: '',
  adjusterName: '',
  adjusterPhone: '',
  adjusterEmail: '',
  dateOfLoss: '',
  propertyAddress: '',
  claimNumber: '',
};

// Extend form values to include the claim name
interface ClaimInfoFormValues extends ClaimSettings {
  claimName: string;
}

export default function ClaimInfoSection() {
  const { activeClaim, refreshClaims, renameClaim } = useClaimContext();
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ClaimInfoFormValues>({
    defaultValues: { ...EMPTY_SETTINGS, claimName: '' },
  });

  // Whenever activeClaim changes (e.g. real-time update from another tab), reset form
  useEffect(() => {
    if (activeClaim) {
      reset({
        claimName: activeClaim.name,
        claimNumber: activeClaim.claimNumber,
        policyNumber: activeClaim.policyNumber,
        insuranceCompany: activeClaim.insuranceCompany,
        adjusterName: activeClaim.adjusterName,
        adjusterPhone: activeClaim.adjusterPhone,
        adjusterEmail: activeClaim.adjusterEmail,
        dateOfLoss: activeClaim.dateOfLoss,
        propertyAddress: activeClaim.propertyAddress,
      });
    } else {
      reset({ ...EMPTY_SETTINGS, claimName: '' });
    }
  }, [activeClaim, reset]);

  const onSubmit = async (data: ClaimInfoFormValues) => {
    if (!activeClaim) return;
    setSaveState('saving');

    // Rename claim if name changed
    const nameChanged = data.claimName.trim() && data.claimName.trim() !== activeClaim.name;
    if (nameChanged) {
      await renameClaim(activeClaim.id, data.claimName.trim());
    }

    const ok = await updateClaim(activeClaim.id, {
      claimNumber: data.claimNumber,
      policyNumber: data.policyNumber,
      insuranceCompany: data.insuranceCompany,
      adjusterName: data.adjusterName,
      adjusterPhone: data.adjusterPhone,
      adjusterEmail: data.adjusterEmail,
      dateOfLoss: data.dateOfLoss,
      propertyAddress: data.propertyAddress,
    });
    if (ok || nameChanged) {
      await refreshClaims();
      setSaveState('saved');
      reset(data);
      setTimeout(() => setSaveState('idle'), 3000);
    } else {
      setSaveState('idle');
    }
  };

  if (!activeClaim) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
        <Loader2 size={16} className="animate-spin" /> Loading claim information…
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="flex items-center gap-2">
        <FileText size={16} className="text-primary" />
        <h2 className="text-base font-semibold text-foreground">Claim Information</h2>
        <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
          {activeClaim.name}
        </span>
        {saveState === 'saved' && (
          <span className="flex items-center gap-1 text-xs text-positive font-medium ml-2 fade-in">
            <Check size={12} /> Saved
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        This information is specific to <strong>{activeClaim.name}</strong> and appears in the export summary sheet.
        Switch claims in the sidebar to manage a different claim.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Claim Name */}
        <div className="space-y-1 md:col-span-2">
          <label htmlFor="claimName" className="block text-sm font-medium text-foreground">
            Claim Name <span className="text-negative">*</span>
          </label>
          <p className="text-xs text-muted-foreground">A descriptive name to identify this claim (e.g. "Smith House Fire")</p>
          <input
            id="claimName"
            type="text"
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring ${errors.claimName ? 'border-negative' : 'border-border'}`}
            {...register('claimName', { required: 'Claim name is required' })}
          />
          {errors.claimName && <p className="text-xs text-negative">{errors.claimName.message}</p>}
        </div>

        {/* Claim Number */}
        <div className="space-y-1">
          <label htmlFor="claimNumber" className="block text-sm font-medium text-foreground">Claim Number</label>
          <p className="text-xs text-muted-foreground">Assigned by your insurance company</p>
          <input
            id="claimNumber"
            type="text"
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono"
            {...register('claimNumber')}
          />
        </div>

        {/* Policy Number */}
        <div className="space-y-1">
          <label htmlFor="policyNumber" className="block text-sm font-medium text-foreground">
            Policy Number <span className="text-negative">*</span>
          </label>
          <p className="text-xs text-muted-foreground">Found on your insurance card or declarations page</p>
          <input
            id="policyNumber"
            type="text"
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono ${errors.policyNumber ? 'border-negative' : 'border-border'}`}
            {...register('policyNumber', { required: 'Policy number is required' })}
          />
          {errors.policyNumber && <p className="text-xs text-negative">{errors.policyNumber.message}</p>}
        </div>

        {/* Insurance Company */}
        <div className="space-y-1">
          <label htmlFor="insuranceCompany" className="block text-sm font-medium text-foreground">
            Insurance Company <span className="text-negative">*</span>
          </label>
          <input
            id="insuranceCompany"
            type="text"
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring ${errors.insuranceCompany ? 'border-negative' : 'border-border'}`}
            {...register('insuranceCompany', { required: 'Insurance company is required' })}
          />
          {errors.insuranceCompany && <p className="text-xs text-negative">{errors.insuranceCompany.message}</p>}
        </div>

        {/* Date of Loss */}
        <div className="space-y-1">
          <label htmlFor="dateOfLoss" className="block text-sm font-medium text-foreground">
            Date of Loss <span className="text-negative">*</span>
          </label>
          <p className="text-xs text-muted-foreground">The date the loss occurred</p>
          <input
            id="dateOfLoss"
            type="date"
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring ${errors.dateOfLoss ? 'border-negative' : 'border-border'}`}
            {...register('dateOfLoss', { required: 'Date of loss is required' })}
          />
          {errors.dateOfLoss && <p className="text-xs text-negative">{errors.dateOfLoss.message}</p>}
        </div>

        {/* Property Address */}
        <div className="space-y-1 md:col-span-2">
          <label htmlFor="propertyAddress" className="block text-sm font-medium text-foreground">Property Address</label>
          <p className="text-xs text-muted-foreground">The address of the damaged property</p>
          <input
            id="propertyAddress"
            type="text"
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
            {...register('propertyAddress')}
          />
        </div>
      </div>

      {/* Adjuster info */}
      <div className="border-t border-border pt-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Insurance Adjuster Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label htmlFor="adjusterName" className="block text-sm font-medium text-foreground">Adjuster Name</label>
            <input
              id="adjusterName"
              type="text"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
              {...register('adjusterName')}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="adjusterPhone" className="block text-sm font-medium text-foreground">Adjuster Phone</label>
            <input
              id="adjusterPhone"
              type="tel"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono"
              {...register('adjusterPhone')}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="adjusterEmail" className="block text-sm font-medium text-foreground">Adjuster Email</label>
            <input
              id="adjusterEmail"
              type="email"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
              {...register('adjusterEmail')}
            />
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <button
          type="submit"
          disabled={saveState === 'saving' || !isDirty}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
        >
          {saveState === 'saving' ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : saveState === 'saved' ? (
            <>
              <Check size={14} />
              Saved
            </>
          ) : (
            <>
              <Save size={14} />
              Save Claim Info
            </>
          )}
        </button>
        {isDirty && saveState === 'idle' && (
          <span className="text-xs text-warning">⚠ Unsaved changes</span>
        )}
      </div>
    </form>
  );
}